/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { CSSProperties, useEffect, useRef, useState } from 'react';

/**
 * PortraitAvatar renders a lightweight "paper-puppet" companion.
 *
 * It stacks three pre-rendered, background-removed frames of the same
 * character (neutral / mouth-open / eyes-closed) and cross-fades between
 * them so the portrait appears to talk (lip-sync driven by RTC volume) and
 * blink (driven by a randomized timer). This is intentionally NOT Live2D:
 * it needs no Cubism runtime, no .moc3 model, and renders on any browser.
 */

export interface PortraitAvatarFrames {
  /** Mouth closed, eyes open. */
  neutral: string;
  /** Mouth open, eyes open (talking). Optional when generation failed. */
  mouthOpen?: string;
  /** Mouth closed, eyes closed (blink). Optional. */
  eyesClosed?: string;
}

/** Rendered content rectangle, expressed as fractions (0..1) of the container. */
export interface ContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface PortraitAvatarProps {
  frames: PortraitAvatarFrames;
  /** Whether the agent is currently speaking. */
  isTalking?: boolean;
  /** RTC linear volume, 0..255. Drives mouth open amount. */
  volume?: number;
  /** Enable periodic blinking. Default true. */
  blink?: boolean;
  className?: string;
  style?: CSSProperties;
  /** object-fit for the frames. Default 'contain'. */
  fit?: 'cover' | 'contain';
  /** Extra scale applied to the portrait. Default 1. */
  zoom?: number;
  /** object-position for the frames. Default 'top center' (top-anchored, good
   *  for a full-stage portrait). Pass 'center' to center-crop like a thumbnail. */
  objectPosition?: string;
  alt?: string;
  /** Reports the rendered content rect (fractions of the container) so callers
   *  can anchor overlays (e.g. a resize handle) to the visible character. */
  onContentRect?: (rect: ContentRect) => void;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Map RTC linear volume (0..255) to a 0..1 mouth-open target. */
function normalizeVolume(v: number) {
  const clamped = Math.max(0, Math.min(255, v));
  return clamp01((clamped - 12) / 78);
}

function PortraitAvatar({
  frames,
  isTalking = false,
  volume = 0,
  blink = true,
  className,
  style,
  fit = 'contain',
  zoom = 1,
  objectPosition = 'top center',
  alt = '',
  onContentRect,
}: PortraitAvatarProps) {
  // Smoothed mouth-open amount (0..1), updated on an rAF loop.
  const [mouth, setMouth] = useState(0);
  const [blinking, setBlinking] = useState(false);
  // Subtle idle breathing only; speech should be expressed by a valid mouth frame.
  const [motionStyle, setMotionStyle] = useState<CSSProperties>({});

  const mouthRef = useRef(0);
  const volumeRef = useRef(0);
  const talkingRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const neutralImgRef = useRef<HTMLImageElement | null>(null);
  const onContentRectRef = useRef(onContentRect);
  onContentRectRef.current = onContentRect;

  volumeRef.current = volume;
  talkingRef.current = isTalking;

  // Report the letterboxed content rect using the frame's actual alignment so callers can
  // anchor overlays to the visible character rather than the padded container.
  useEffect(() => {
    const report = onContentRectRef.current;
    if (!report) return undefined;
    const measure = () => {
      const container = containerRef.current;
      const img = neutralImgRef.current;
      if (!container || !img) return;
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!cw || !ch || !nw || !nh) return;
      const base = fit === 'cover' ? Math.max(cw / nw, ch / nh) : Math.min(cw / nw, ch / nh);
      const [positionX, positionY] = window.getComputedStyle(img).objectPosition.split(/\s+/);
      // Browsers normalize keyword positions (e.g. center bottom) to percentages.
      const alignment = (position: string | undefined, fallback: number) =>
        position?.endsWith('%') ? Number.parseFloat(position) / 100 : fallback;
      const frameZoom = zoom || 1;
      const w = base * nw * frameZoom;
      const h = base * nh * frameZoom;
      // The frame wrapper scales around top-center, matching motionStyle below.
      const left =
        cw / 2 + ((cw - base * nw) * alignment(positionX, 0.5) - cw / 2) * frameZoom;
      const top = (ch - base * nh) * alignment(positionY, 0) * frameZoom;
      const right = clamp01((left + w) / cw);
      const bottom = clamp01((top + h) / ch);
      report({
        left: clamp01(left / cw),
        top: clamp01(top / ch),
        width: Math.max(0, right - clamp01(left / cw)),
        height: Math.max(0, bottom - clamp01(top / ch)),
      });
    };
    measure();
    const img = neutralImgRef.current;
    img?.addEventListener('load', measure);
    let ro: ResizeObserver | null = null;
    if (containerRef.current && 'ResizeObserver' in window) {
      ro = new ResizeObserver(measure);
      ro.observe(containerRef.current);
    }
    return () => {
      img?.removeEventListener('load', measure);
      ro?.disconnect();
    };
  }, [fit, zoom, frames.neutral, objectPosition]);

  // rAF loop: ease the mouth-open value toward the volume-driven target and
  // add a subtle wobble while talking so it doesn't look mechanically static.
  useEffect(() => {
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(64, now - last);
      last = now;
      const hasMouthFrame = Boolean(frames.mouthOpen);
      const talking = talkingRef.current;
      const target = talking && hasMouthFrame ? normalizeVolume(volumeRef.current) : 0;
      // Open faster than it closes for a snappier feel.
      const speed = target > mouthRef.current ? 0.02 : 0.012;
      const k = clamp01(dt * speed);
      mouthRef.current += (target - mouthRef.current) * k;
      let value = mouthRef.current;
      if (talking && value > 0.06) {
        value = clamp01(value + 0.1 * Math.abs(Math.sin(now * 0.018)));
      }
      setMouth((prev) => (Math.abs(prev - value) > 0.01 ? value : prev));

      // Whole-portrait micro-motion stays idle-only; avoid fake full-avatar
      // scale pulses when speech frames are missing or invalid.
      const breathe = Math.sin(now * 0.0016);
      const translateY = breathe * 0.45;
      const scale = (zoom || 1) * (1 + breathe * 0.0035);
      setMotionStyle({
        transform: `translateY(${translateY.toFixed(2)}px) scale(${scale.toFixed(4)})`,
        transformOrigin: 'top center',
      });

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [frames.mouthOpen, zoom]);

  // Randomized blink loop.
  useEffect(() => {
    if (!blink || !frames.eyesClosed) return;
    let closeTimer: ReturnType<typeof setTimeout>;
    let openTimer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    const scheduleNext = () => {
      const delay = 2200 + Math.random() * 3200;
      closeTimer = setTimeout(() => {
        if (cancelled) return;
        setBlinking(true);
        openTimer = setTimeout(() => {
          if (cancelled) return;
          setBlinking(false);
          scheduleNext();
        }, 90);
      }, delay);
    };
    scheduleNext();
    return () => {
      cancelled = true;
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
    };
  }, [blink, frames.eyesClosed]);

  const layerBase: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: fit,
    objectPosition,
    userSelect: 'none',
    pointerEvents: 'none',
    WebkitUserSelect: 'none',
  };
  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          willChange: 'transform',
          ...motionStyle,
        }}
      >
        {/* neutral base */}
        <img
          ref={neutralImgRef}
          src={frames.neutral}
          alt={alt}
          draggable={false}
          style={layerBase}
        />
        {/* mouth-open layer, opacity driven by volume */}
        {frames.mouthOpen ? (
          <img
            src={frames.mouthOpen}
            alt=""
            aria-hidden
            draggable={false}
            style={{
              ...layerBase,
              opacity: mouth,
              transition: 'opacity 40ms linear',
            }}
          />
        ) : null}
        {/* eyes-closed blink layer: kept semi-transparent so the whole-body
            drift between the neutral/eyesClosed re-renders is diluted to a
            faint shimmer while the eye area (high contrast) still reads. */}
        {frames.eyesClosed ? (
          <img
            src={frames.eyesClosed}
            alt=""
            aria-hidden
            draggable={false}
            style={{
              ...layerBase,
              opacity: blinking ? 0.55 : 0,
              transition: 'opacity 80ms ease-in-out',
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

export default PortraitAvatar;
